import { useQuery, keepPreviousData, type UseQueryOptions } from '@tanstack/react-query';
import { subscriptionsApi } from '@/lib/api/services/subscriptions';
import type { Subscription } from '@/lib/api/services/subscriptions';

export const CURRENT_SUBSCRIPTION_KEY = ['current-subscription'] as const;

const PENDING_FAST_POLL_MS = 15 * 1000;
const PENDING_SLOW_POLL_MS = 60 * 1000;
const PENDING_FAST_WINDOW_MS = 2 * 60 * 1000;
let pendingSince: number | null = null;

/*
  Poll interval while a payment waits for its webhook: every 15 s for the first 2
  minutes, then every minute. Every caller polling the subscription uses this one
  function, so their timers line up on the shared query and React Query sends a
  single request per tick instead of one per mounted component.
*/
export function pendingSubscriptionRefetchInterval(query: {
  state: { data: Subscription | null | undefined };
}): number | false {
  if ((query.state.data as { status?: string } | null | undefined)?.status !== 'pending') {
    pendingSince = null;
    return false;
  }
  const now = Date.now();
  if (pendingSince === null) pendingSince = now;
  return now - pendingSince < PENDING_FAST_WINDOW_MS ? PENDING_FAST_POLL_MS : PENDING_SLOW_POLL_MS;
}

type HookOptions = {
  enabled?: boolean;

  staleTime?: UseQueryOptions['staleTime'];

  refetchInterval?: UseQueryOptions<Subscription | null>['refetchInterval'];
};

export function useCurrentSubscription(opts: HookOptions = {}) {
  const { enabled = true, staleTime = 2 * 60 * 1000, refetchInterval } = opts;

  return useQuery<Subscription | null>({
    queryKey: CURRENT_SUBSCRIPTION_KEY,
    queryFn: async () => {
      const result = await subscriptionsApi.getCurrentSubscription();
      const err = (result as any)?.error;
      if (err && err === 'No active subscription found') return null;
      if (err) throw new Error(err);
      return result.data ?? null;
    },
    enabled,
    staleTime,
    // The app-wide default is refetchOnWindowFocus:false, which meant a tab
    // left open across a payment made elsewhere, a trial lapsing or a
    // cancellation kept rendering the old plan until a hard reload. This one
    // query is the client's view of the server's access decision, so it
    // re-checks when the user comes back (still subject to staleTime).
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    retry: (failureCount, error) => {
      const msg = (error as Error)?.message || '';
      if (/unauthor|401|forbidden|403/i.test(msg)) return false;
      return failureCount < 2;
    },
    ...(refetchInterval !== undefined && { refetchInterval }),
  });
}
